import React, { lazy, Suspense, useEffect, useState } from 'react'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import {
  RedirectAdminInvestorLogin,
  RedirectFirstTimeInvestor,
} from './lib/routes/legacyInvestorRedirects'
import { useSelector, useDispatch } from 'react-redux'
import { GoogleOAuthProvider } from '@react-oauth/google'

import { Navbar, Footer, WarningIcon, Anchor, ContactUsModal } from './lib/components'
import MaintenancePage from './lib/components/MaintenancePage'
import { GlobalPhoneCollectionModal } from './lib/components/PhoneCollectionModal'
import {
  PublicRoute,
  PrivateRoute,
  KYC,
  NO_NDA,
  NDA,
  EmailVerified,
  AccountNotDormant,
} from './route-permissions'
import { RootState } from './store'
import ScrollToTop from './utils/ScrollToTop'
import { lazyNamed } from './lib/utils/lazy-named'
import { ChunkErrorBoundary } from './lib/components/ChunkErrorBoundary'
import { useGoogleAnalyticsPageViews } from './lib/analytics/use-google-analytics-page-views'
import { captureUtmAttribution } from './lib/utils/utm-attribution'
import { FeatureName, isFeatureEnabled } from './lib/utils/feature-flags'
import { kycServices, userServices, intercomService } from './services'
import {
  createConfiguration,
  resetConfiguration,
  updateConfiguration,
} from './global-state/investor-slice'
import {
  applyInvestorKycStateToStore,
  fetchInvestorKycState,
} from './lib/utils/sync-investor-kyc-state'
import { getPostLoginNavigation } from './lib/utils/post-login'
import { isSuperuserSession, resolveRiskProfilerRequired } from './lib/utils/onboarding'
import { FINISH_SETTING_UP_PROFILE_PATH } from './lib/utils/message'
import './App.scss'
import { DormancyAcknowledgementModal, DormancyReminderModal } from './lib/components/DormancyModal'
import {
  hidePhoneCollectionModal,
  setPhonePreference,
  showPhoneCollectionModal,
} from './global-state/phone-collection-slice'

// Pages are code-split per route so the first visit (e.g. /promote) does not download the whole app.
const LoginV2Container = lazyNamed(
  () => import('./pages/login-v2/login-v2-container'),
  'LoginV2Container',
)
const SignupV2Container = lazyNamed(
  () => import('./pages/signup-v2/signup-v2-container'),
  'SignupV2Container',
)
const VerifyEmailOtpContainer = lazyNamed(
  () => import('./pages/verify-email-opt/verify-email-opt-container'),
  'VerifyEmailOtpContainer',
)
const VerifyEmailContainer = lazyNamed(
  () => import('./pages/verify-email/verify-email-container'),
  'VerifyEmailContainer',
)
const ForgotPasswordContainer = lazyNamed(
  () => import('./pages/forgot-password/forgot-password-container'),
  'ForgotPasswordContainer',
)
const ResetPasswordContainer = lazyNamed(
  () => import('./pages/reset-password/reset-password-container'),
  'ResetPasswordContainer',
)
const ResetInstructionsContainer = lazyNamed(
  () => import('./pages/reset-instructions/reset-instruction-container'),
  'ResetInstructionsContainer',
)
const NotFound = lazyNamed(() => import('./pages/not-found/not-found-page'), 'NotFound')
const AccessDenied = lazyNamed(
  () => import('./pages/access-denied/access-denied-page'),
  'AccessDenied',
)
const AcceptNDAContainer = lazyNamed(
  () => import('./pages/accept-nda/accept-nda-container'),
  'AcceptNDAContainer',
)
const FAQ = lazyNamed(() => import('./pages/faq/faq-presenter'), 'FAQ')
const FAQdetails = lazyNamed(() => import('./pages/faq/faq-details-presenter'), 'FAQdetails')
const ProfileContainer = lazyNamed(
  () => import('./pages/profile/profile-container'),
  'ProfileContainer',
)
const InvestV2Container = lazyNamed(
  () => import('./pages/invest-v2/invest-v2-container'),
  'InvestV2Container',
)
const DashboardV2Container = lazyNamed(
  () => import('./pages/dashboard-v2/dashboard-v2-container'),
  'DashboardV2Container',
)
const PaymentContainer = lazyNamed(
  () => import('./pages/payment/payment-container'),
  'PaymentContainer',
)
const PrivacyPolicyContainer = lazyNamed(
  () => import('./pages/privacy-policy/privacy-policy-container'),
  'PrivacyPolicyContainer',
)
const TermsAndConditionsContainer = lazyNamed(
  () => import('./pages/terms-and-conditions/terms-and-conditions-container'),
  'TermsAndConditionsContainer',
)
const DisclaimerContainer = lazyNamed(
  () => import('./pages/disclaimer/disclaimer-container'),
  'DisclaimerContainer',
)
const OpportunityDetailContainer = lazyNamed(
  () => import('./pages/opportunity-detail/opportunity-detail-container'),
  'OpportunityDetailContainer',
)
const OpportunityListV2Container = lazyNamed(
  () => import('./pages/opportunities-v2/opportunity-list-v2-container'),
  'OpportunityListV2Container',
)
const CloseTabComponent = lazyNamed(
  () => import('./pages/invest/close-tab-component'),
  'CloseTabComponent',
)
const ContactUsContainer = lazyNamed(
  () => import('./pages/contact-us/contact-us-container'),
  'ContactUsContainer',
)
const ResendVerificationContainer = lazyNamed(
  () => import('./pages/resend-verification-email/resend-verification-email-container'),
  'ResendVerificationContainer',
)
const AboutUsV2 = lazyNamed(() => import('./pages/about-us-v2/about-us-v2'), 'AboutUsV2')
const TermsOfBusiness = lazyNamed(
  () => import('./pages/terms-of-business/terms-of-business'),
  'TermsOfBusiness',
)
const WithdrawContainer = lazyNamed(
  () => import('./pages/withdraw/withdraw-container'),
  'WithdrawContainer',
)
const AdminLoginContainer = lazyNamed(
  () => import('./pages/admin-login/admin-login-container'),
  'AdminLoginContainer',
)
const FirstTimeInvestor = lazyNamed(
  () => import('./pages/first-time-investor/first-time-investor'),
  'FirstTimeInvestor',
)
const HomeV2Container = lazyNamed(
  () => import('./pages/home-v2/home-v2-container'),
  'HomeV2Container',
)
const SecondaryMarketContainer = lazyNamed(
  () => import('./pages/secondary-market/secondary-market-container'),
  'SecondaryMarketContainer',
)
const WalletContainer = lazyNamed(
  () => import('./pages/wallet/wallet-container'),
  'WalletContainer',
)
const PartnerLoginContainer = lazyNamed(
  () => import('./pages/partner/partner-login-container'),
  'PartnerLoginContainer',
)
const PartnerSetPasswordContainer = lazyNamed(
  () => import('./pages/partner/partner-set-password-container'),
  'PartnerSetPasswordContainer',
)
const PartnerInvestorsContainer = lazyNamed(
  () => import('./pages/partner/partner-investors-container'),
  'PartnerInvestorsContainer',
)
const TradingContainer = lazyNamed(
  () => import('./pages/trading/trading-container'),
  'TradingContainer',
)
const CompanyContainer = lazyNamed(
  () => import('./pages/trading/company-container'),
  'CompanyContainer',
)
const PortfolioContainer = lazyNamed(
  () => import('./pages/trading/portfolio-container'),
  'PortfolioContainer',
)
const WalletPublicContainer = lazyNamed(
  () => import('./pages/wallet-public/wallet-public-container'),
  'WalletPublicContainer',
)
const BasicDetailsContainer = lazyNamed(
  () => import('./pages/onboarding/basic-details/basic-details-container'),
  'BasicDetailsContainer',
)
const RiskProfilerContainer = lazyNamed(
  () => import('./pages/onboarding/risk-profiler/risk-profiler-container'),
  'RiskProfilerContainer',
)
const BlogPage = lazyNamed(() => import('./pages/blog/blog'), 'BlogPage')
const BlogDetailsPage = lazyNamed(
  () => import('./pages/blog-details/blog-details'),
  'BlogDetailsPage',
)
const NewsletterPage = lazyNamed(() => import('./pages/newsletter/newsletter'), 'NewsletterPage')
const WebinarsPage = lazyNamed(() => import('./pages/webinars/webinars'), 'WebinarsPage')
const DashboardSpecificInvestmentV2Container = lazyNamed(
  () => import('./pages/dashboard-v2/specific-investment-v2-container'),
  'DashboardSpecificInvestmentV2Container',
)
const PromoteContainer = lazyNamed(
  () => import('./pages/promote/promote-container'),
  'PromoteContainer',
)
const PromoteSetPasswordContainer = lazyNamed(
  () => import('./pages/promote/promote-set-password-container'),
  'PromoteSetPasswordContainer',
)
const SetOwnPasswordContainer = lazyNamed(
  () => import('./pages/promote/set-own-password-container'),
  'SetOwnPasswordContainer',
)
const OpportunityInterestContainer = lazy(
  () => import('./pages/opportunity-interest/opportunity-interest-container'),
)

// Add helper function for checking skip date
const isSkipDateExpired = (skipDate: string): boolean => {
  if (!skipDate) return false

  const skipDateTime = new Date(skipDate).getTime()
  const currentTime = new Date().getTime()
  const millisecondsInDay = 1000 * 60 * 60 * 24
  const daysDifference = (currentTime - skipDateTime) / millisecondsInDay

  // Show the modal after 15 days
  const requiredDays = 15
  return daysDifference >= requiredDays
}

/** Renders the route only when its feature flag is on; otherwise a 404. */
const FeatureRoute = ({ feature, children }: { feature: FeatureName; children: JSX.Element }) =>
  isFeatureEnabled(feature) ? children : <NotFound />

const RedirectToInvestV2 = () => {
  const { projectSlug, projectId } = useParams()
  const { search } = useLocation()
  const target = projectId
    ? `/invest-v2/${projectSlug}/${projectId}`
    : `/invest-v2/link/${projectSlug}`
  return <Navigate to={`${target}${search}`} replace />
}

const AppShell = () => {
  // Check if maintenance mode is enabled via environment variable
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true'

  // If maintenance mode is enabled, show maintenance page
  if (isMaintenanceMode) {
    return <MaintenancePage />
  }

  const {
    authToken,
    isEmailVerified,
    kycStatus,
    accountStatus,
    name,
    email,
    userId,
    phone,
    basicDetailsCompleted,
    riskProfileCompleted,
    riskProfilerRequired,
    adminRole,
  } = useSelector((state: RootState) => state.investor)
  const { preference } = useSelector((state: RootState) => state.phoneCollection)
  const [showContactUsModal, setShowContactUsModal] = useState(false)
  const [clientAgreementSigned, setClientAgreementSigned] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isLoggedIn = authToken && authToken
  const location = useLocation()
  const { pathname } = location
  useGoogleAnalyticsPageViews()

  useEffect(() => {
    captureUtmAttribution(location.search, location.pathname)
  }, [location.search, location.pathname])
  const normalizedPathname =
    pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname
  const isAuthFullscreenRoute =
    normalizedPathname.startsWith('/login') ||
    normalizedPathname.startsWith('/signup') ||
    normalizedPathname.startsWith('/promote') ||
    normalizedPathname === '/verify-account' ||
    normalizedPathname === '/welcome' ||
    normalizedPathname === '/set-password' ||
    normalizedPathname.startsWith('/onboarding')
  const isPartnerRoute =
    normalizedPathname === '/partner' || normalizedPathname.startsWith('/partner/')

  const isInvestV2Route = normalizedPathname.startsWith('/invest-v2')
  const isOnboardingStep = normalizedPathname.startsWith('/onboarding')
  const isRiskProfilerPending = resolveRiskProfilerRequired({
    riskProfilerRequired,
    riskProfileCompleted,
  })

  const resendVerificationEmailHandler = async () => {
    const data = await userServices.userLogout_v1({
      Authorization: authToken ? authToken : '',
    })
    if (data.status === 200) {
      dispatch(resetConfiguration())
      window.location.href = 'resend-verification-email'
    }
  }

  useEffect(() => {
    if (!isPartnerRoute && !isEmailVerified && authToken) {
      const intervalId = setInterval(async () => {
        const investorWithKyc = await kycServices.getInvestorWithKyc({
          Authorization: authToken,
        })

        if (investorWithKyc?.status === 200) {
          const { acceptedNDA, kycStatus, ...investorKYCData } = investorWithKyc.data
          dispatch(
            createConfiguration({
              authToken,
              isEmailVerified: investorWithKyc.data.is_verified,
              acceptedNDA: acceptedNDA,
              kycStatus: kycStatus,
              ...investorKYCData,
            }),
          )
        }
      }, 1000)
      return () => clearInterval(intervalId)
    }
  }, [authToken, dispatch, isEmailVerified, isPartnerRoute])

  useEffect(() => {
    const redirectIncompleteOnboarding = async () => {
      if (!isLoggedIn || !isEmailVerified || !authToken || !userId) return
      // /verify-email signs the investor in and then routes them itself (e.g. to /set-password).
      if (
        isAuthFullscreenRoute ||
        isPartnerRoute ||
        normalizedPathname === '/verify-email' ||
        normalizedPathname.startsWith('/onboarding')
      )
        return

      const postLogin = await getPostLoginNavigation(
        authToken,
        userId,
        email,
        name,
        isSuperuserSession(adminRole),
      )
      dispatch(
        updateConfiguration({
          name: 'basicDetailsCompleted',
          value: postLogin.basicDetailsCompleted,
        }),
      )
      dispatch(
        updateConfiguration({
          name: 'riskProfileCompleted',
          value: postLogin.riskProfileCompleted,
        }),
      )
      dispatch(
        updateConfiguration({
          name: 'riskProfilerRequired',
          value: postLogin.riskProfilerRequired,
        }),
      )

      if (postLogin.path.startsWith('/onboarding')) {
        navigate(postLogin.path, { replace: true })
      }
    }

    redirectIncompleteOnboarding()
  }, [
    isLoggedIn,
    isEmailVerified,
    authToken,
    userId,
    email,
    name,
    adminRole,
    normalizedPathname,
    isAuthFullscreenRoute,
    isPartnerRoute,
    dispatch,
    navigate,
  ])

  useEffect(() => {
    const checkPhoneCollectionStatus = async () => {
      if (isPartnerRoute) return
      if (!authToken) return // Don't run if not authenticated
      if (normalizedPathname.startsWith('/onboarding')) return
      if (userId && !basicDetailsCompleted) {
        return
      }

      try {
        const response = await userServices.getUserProfile_v1({
          Authorization: authToken || '',
        })

        if (response && response.status === 200 && response.data) {
          // Handle different phone preference scenarios
          const handlePhonePreference = () => {
            const { phoneNumber, phonePreference, phoneSkipDate } = response.data

            // Case 1: Phone exists and preference is saved
            if (phoneNumber && phonePreference === 'saved') {
              dispatch(setPhonePreference('saved'))
              dispatch(hidePhoneCollectionModal())
              return true
            }

            // Case 2: Not interested in providing phone
            if (phonePreference === 'not-interested') {
              dispatch(setPhonePreference('not-interested'))
              dispatch(hidePhoneCollectionModal())
              return true
            }

            // Case 3: Skipped providing phone
            if (phonePreference === 'skipped') {
              dispatch(setPhonePreference('skipped'))

              if (isSkipDateExpired(phoneSkipDate)) {
                // It's been 15+ days since skip, show the modal
                dispatch(showPhoneCollectionModal())
              } else {
                dispatch(hidePhoneCollectionModal())
              }
              return true
            }

            // Default case: Need to collect phone number
            dispatch(showPhoneCollectionModal())
            return false
          }

          handlePhonePreference()
        }
      } catch (error) {
        // Fail silently - don't show an error banner
        console.error('Error checking phone collection status:', error)
      }
    }

    checkPhoneCollectionStatus()
  }, [authToken, dispatch, normalizedPathname, userId, basicDetailsCompleted, isPartnerRoute])

  // Intercom integration - boot when user logs in, shutdown when logs out
  useEffect(() => {
    if (isPartnerRoute) {
      intercomService.shutdown()
      return
    }
    if (isLoggedIn && email && userId) {
      // Boot Intercom with user context
      intercomService.boot({
        user_id: userId,
        email: email,
        name: name || email,
        created_at: Math.floor(Date.now() / 1000),
        kyc_status: kycStatus,
        account_status: accountStatus,
        current_route: pathname,
        phone: phone,
      })
    } else {
      // Shutdown Intercom when user logs out
      intercomService.shutdown()
    }
  }, [isLoggedIn, email, userId, name, kycStatus, accountStatus, phone, isPartnerRoute])

  // Update Intercom context when route changes
  useEffect(() => {
    if (isPartnerRoute) return
    if (isLoggedIn) {
      intercomService.update({
        current_route: pathname,
      })
      // Manage visibility based on route
      intercomService.manageVisibility(pathname)
    }
  }, [pathname, isLoggedIn, isPartnerRoute])

  // Sync KYC status, NDA acceptance, and client agreement from API (Redux can be stale after login)
  useEffect(() => {
    const syncKycState = async () => {
      if (isPartnerRoute || !isLoggedIn || !userId || !authToken) return

      try {
        const kycState = await fetchInvestorKycState(userId, authToken)
        if (!kycState) return

        applyInvestorKycStateToStore(dispatch, kycState)
        setClientAgreementSigned(kycState.clientAgreementSigned)
      } catch (error) {
        console.error('Error syncing KYC state:', error)
      }
    }

    syncKycState()
  }, [isLoggedIn, userId, authToken, dispatch, isPartnerRoute])

  // Poll client agreement status when on KYC page and agreement not signed
  useEffect(() => {
    if (!isLoggedIn || !userId || clientAgreementSigned || pathname !== '/profile/kyc') {
      return
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await kycServices.getKYC_v1(userId.toString(), {
          Authorization: authToken || '',
        })

        if (response?.data?.client_agreement_signed) {
          setClientAgreementSigned(true)
          clearInterval(pollInterval)
        }
      } catch (error) {
        console.error('Error polling client agreement status:', error)
      }
    }, 3000) // Poll every 3 seconds

    return () => clearInterval(pollInterval)
  }, [isLoggedIn, userId, authToken, clientAgreementSigned, pathname])

  return (
    <>
      {!isPartnerRoute && isLoggedIn && !isEmailVerified && (
        <div className='verify-email-top-warning-banner'>
          <WarningIcon type='warning-banner' />{' '}
          <span>
            To start investing, please verify your email using the link we sent to{' '}
            {email || 'your email address'}. If you can&apos;t find it, check your spam folder or{' '}
            <a
              href='#'
              className='basic-anchor'
              onClick={(e) => {
                e.preventDefault()
                resendVerificationEmailHandler()
              }}
              role='button'
            >
              click here
            </a>{' '}
            to resend it.
          </span>
        </div>
      )}
      {!isPartnerRoute && isLoggedIn && isEmailVerified && accountStatus === 'DORMANT' && (
        <div className='verify-email-top-warning-banner'>
          <WarningIcon type='warning-banner' />{' '}
          <span>
            Your account is currently marked dormant. Please contact{' '}
            <Anchor variant='basic-anchor' text='Credibila Support' href='/contact-us' /> to
            activate it.
          </span>
        </div>
      )}
      {!isPartnerRoute &&
        isLoggedIn &&
        isEmailVerified &&
        !isOnboardingStep &&
        isRiskProfilerPending &&
        accountStatus === 'ACTIVE' && (
          <div className='verify-email-top-warning-banner'>
            <WarningIcon type='warning-banner' />{' '}
            <span>
              Please{' '}
              <Anchor
                variant='basic-anchor'
                text='finish setting up your profile'
                href={FINISH_SETTING_UP_PROFILE_PATH}
              />{' '}
              to begin investing.
            </span>
          </div>
        )}
      {!isPartnerRoute &&
        isLoggedIn &&
        isEmailVerified &&
        !isOnboardingStep &&
        !isRiskProfilerPending &&
        !clientAgreementSigned &&
        accountStatus === 'ACTIVE' && (
          <div className='verify-email-top-warning-banner'>
            <WarningIcon type='warning-banner' />{' '}
            {pathname !== '/profile/kyc' ? (
              <span>
                Please complete the{' '}
                <Anchor
                  variant='basic-anchor'
                  text='Client Agreement & Self Declaration form'
                  href='/profile/kyc'
                />{' '}
                to begin investing.
              </span>
            ) : (
              <span>
                Please complete the Client Agreement & Self Declaration form to begin investing.
              </span>
            )}
          </div>
        )}
      {!isPartnerRoute &&
        isLoggedIn &&
        isEmailVerified &&
        !isOnboardingStep &&
        !isRiskProfilerPending &&
        clientAgreementSigned &&
        kycStatus !== 'APPROVED' &&
        accountStatus === 'ACTIVE' && (
          <div className='verify-email-top-warning-banner'>
            <WarningIcon type='warning-banner' />{' '}
            {pathname !== '/profile/kyc' ? (
              <span>
                {kycStatus === 'EXPIRED' && (
                  <>
                    Your KYC documents are no longer valid. Please resubmit the{' '}
                    <Anchor variant='basic-anchor' text='new KYC documents' href='/profile/kyc' />{' '}
                    to begin investing.
                  </>
                )}
                {kycStatus === 'RESUBMIT_SOF' && (
                  <>
                    Please resubmit the{' '}
                    <Anchor
                      variant='basic-anchor'
                      text='Source of Funds statement'
                      href='/profile/kyc'
                    />{' '}
                    to initiate the investment.
                  </>
                )}
                {kycStatus !== 'EXPIRED' && kycStatus !== 'RESUBMIT_SOF' && (
                  <>
                    Please complete the{' '}
                    <Anchor variant='basic-anchor' text='KYC requirements' href='/profile/kyc' /> to
                    begin investing.
                  </>
                )}
              </span>
            ) : (
              <span>
                {kycStatus === 'EXPIRED' &&
                  'Your KYC documents are no longer valid. Please resubmit the new KYC documents to begin investing.'}
                {kycStatus === 'RESUBMIT_SOF' &&
                  'Please resubmit the Source of Funds statement to initiate the investment.'}
                {kycStatus !== 'EXPIRED' &&
                  kycStatus !== 'RESUBMIT_SOF' &&
                  'Please complete the KYC requirements to begin investing.'}
              </span>
            )}
          </div>
        )}

      {!isAuthFullscreenRoute && !isPartnerRoute && (
        <Navbar setShowContactUsModal={(show) => setShowContactUsModal(show)} />
      )}
      <div
        className={
          isAuthFullscreenRoute || isPartnerRoute
            ? 'App App--fullscreen'
            : isInvestV2Route
            ? 'App App--invest-v2'
            : 'App'
        }
      >
        <ScrollToTop />
        <ChunkErrorBoundary resetKey={pathname}>
          <Suspense fallback={<div style={{ minHeight: '60vh' }} aria-busy='true' />}>
            <Routes>
              <Route
                path='signup'
                element={
                  <PublicRoute>
                    <SignupV2Container />
                  </PublicRoute>
                }
              />
              <Route
                path='signup/:email'
                element={
                  <PublicRoute>
                    <SignupV2Container />
                  </PublicRoute>
                }
              />
              <Route
                path='promote'
                element={
                  <PublicRoute>
                    <PromoteContainer />
                  </PublicRoute>
                }
              />
              <Route
                path='promote/signup'
                element={
                  <PublicRoute>
                    <PromoteContainer />
                  </PublicRoute>
                }
              />
              <Route
                path='promote/set-password'
                element={
                  <PublicRoute>
                    <PromoteSetPasswordContainer />
                  </PublicRoute>
                }
              />
              <Route path='set-password' element={<SetOwnPasswordContainer />} />
              <Route
                path='login'
                element={
                  <PublicRoute>
                    <LoginV2Container />
                  </PublicRoute>
                }
              />
              <Route
                path='/onboarding/basic-details'
                element={
                  <PrivateRoute perms={[EmailVerified]}>
                    <BasicDetailsContainer />
                  </PrivateRoute>
                }
              />
              <Route
                path='/onboarding/risk-profiler'
                element={
                  <PrivateRoute perms={[EmailVerified]}>
                    <RiskProfilerContainer />
                  </PrivateRoute>
                }
              />
              <Route path='admin/investor-login' element={<RedirectAdminInvestorLogin />} />
              <Route
                path='admin/client-login'
                element={
                  <PublicRoute>
                    <AdminLoginContainer />
                  </PublicRoute>
                }
              />
              <Route
                path='welcome'
                element={
                  <PublicRoute>
                    <VerifyEmailOtpContainer />
                  </PublicRoute>
                }
              />
              <Route
                path='verify-account'
                element={
                  <PublicRoute>
                    <VerifyEmailOtpContainer />
                  </PublicRoute>
                }
              />
              <Route
                path='verify-email'
                element={
                  <PublicRoute>
                    <VerifyEmailContainer />
                  </PublicRoute>
                }
              />
              <Route
                path='forgot-password'
                element={
                  <PublicRoute>
                    <ForgotPasswordContainer />
                  </PublicRoute>
                }
              />
              <Route
                path='resend-verification-email'
                element={
                  <PublicRoute>
                    <ResendVerificationContainer />
                  </PublicRoute>
                }
              />
              <Route
                path='reset-instructions'
                element={
                  <PublicRoute>
                    <ResetInstructionsContainer />
                  </PublicRoute>
                }
              />
              <Route
                path='reset-password'
                element={
                  <PublicRoute>
                    <ResetPasswordContainer />
                  </PublicRoute>
                }
              />
              <Route path='opportunities' element={<OpportunityListV2Container />} />
              {/* <Route path='opportunities-v2' element={<OpportunityListV2Container />} /> */}
              <Route
                path='accept-NDA'
                element={
                  <PrivateRoute perms={[NO_NDA, EmailVerified]}>
                    <AcceptNDAContainer />
                  </PrivateRoute>
                }
              />
              <Route
                path='opportunity/link/:projectSlug'
                element={
                  <PrivateRoute perms={[EmailVerified]}>
                    <OpportunityDetailContainer />
                  </PrivateRoute>
                }
              />
              <Route
                path='opportunity/:projectSlug/:id'
                element={
                  <PrivateRoute perms={[NDA, EmailVerified]}>
                    <OpportunityDetailContainer />
                  </PrivateRoute>
                }
              />
              {/* Replace /invest with invest page component */}
              <Route path='/invest/signing-complete' element={<CloseTabComponent />} />
              {/* The old flow skipped the wallet payment step; send old links to the current wizard. */}
              <Route path='/invest/link/:projectSlug' element={<RedirectToInvestV2 />} />
              <Route path='/invest/:projectSlug/:projectId' element={<RedirectToInvestV2 />} />
              <Route
                path='/invest-v2/link/:projectSlug'
                element={
                  <PrivateRoute perms={[EmailVerified, KYC, AccountNotDormant]}>
                    <InvestV2Container />
                  </PrivateRoute>
                }
              />
              <Route
                path='/invest-v2/:projectSlug/:projectId'
                element={
                  <PrivateRoute perms={[EmailVerified, KYC, AccountNotDormant]}>
                    <InvestV2Container />
                  </PrivateRoute>
                }
              />
              <Route
                path='/payment/:projectSlug/:projectId/:paymentPage'
                element={
                  <PrivateRoute perms={[EmailVerified, KYC, AccountNotDormant]}>
                    <PaymentContainer />
                  </PrivateRoute>
                }
              />
              <Route
                path='/withdraw/:projectSlug/:projectId'
                element={
                  <PrivateRoute perms={[EmailVerified, KYC, AccountNotDormant]}>
                    <WithdrawContainer />
                  </PrivateRoute>
                }
              />
              <Route path='/403' element={<AccessDenied />} />
              <Route path='/faq' element={<FAQ />} />
              <Route path='/faq-details/:title' element={<FAQdetails />} />
              <Route path='/first-time-investor' element={<RedirectFirstTimeInvestor />} />
              <Route path='/first-time-client' element={<FirstTimeInvestor />} />
              <Route path='/insights' element={<BlogPage />} />
              <Route path='/insights/posts/:slug' element={<BlogDetailsPage />} />
              <Route path='/newsletter' element={<NewsletterPage />} />
              <Route path='/webinars/:slug' element={<WebinarsPage />} />
              <Route path='/webinars' element={<WebinarsPage />} />
              <Route path='/terms-of-business' element={<TermsOfBusiness />} />
              <Route
                path='/contact-us'
                element={
                  <PrivateRoute perms={[EmailVerified]}>
                    <ContactUsContainer />
                  </PrivateRoute>
                }
              />
              <Route path='submit-opportunity' element={<OpportunityInterestContainer />} />
              <Route path='access-denied' element={<AccessDenied />} />
              <Route path='/' element={<HomeV2Container />} />
              <Route path='/home' element={<HomeV2Container />} />
              {/* <Route path='/home-v2' element={<HomeV2Container />} /> */}
              <Route path='/home/session-expired' element={<HomeV2Container />} />
              <Route path='/privacy_policy' element={<PrivacyPolicyContainer />} />
              <Route path='/terms_and_conditions' element={<TermsAndConditionsContainer />} />
              <Route path='/disclaimer' element={<DisclaimerContainer />} />
              <Route
                path='/profile/:profileTab'
                element={
                  <PrivateRoute perms={[EmailVerified]}>
                    <ProfileContainer />
                  </PrivateRoute>
                }
              />
              <Route
                path='/dashboard'
                element={
                  <PrivateRoute perms={[EmailVerified]}>
                    <DashboardV2Container />
                  </PrivateRoute>
                }
              />
              <Route
                path='/dashboard/wallet'
                element={
                  <PrivateRoute perms={[EmailVerified, KYC, AccountNotDormant]}>
                    <WalletContainer />
                  </PrivateRoute>
                }
              />
              <Route
                path='/dashboard/wallet-public'
                element={
                  <FeatureRoute feature='publicWallet'>
                    <PrivateRoute perms={[EmailVerified, KYC, AccountNotDormant]}>
                      <WalletPublicContainer />
                    </PrivateRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path='/dashboard/trading'
                element={
                  <FeatureRoute feature='trading'>
                    <PrivateRoute perms={[EmailVerified, KYC, AccountNotDormant]}>
                      <TradingContainer />
                    </PrivateRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path='/dashboard/trading/portfolio'
                element={
                  <FeatureRoute feature='trading'>
                    <PrivateRoute perms={[EmailVerified, KYC, AccountNotDormant]}>
                      <PortfolioContainer />
                    </PrivateRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path='/dashboard/trading/:symbol'
                element={
                  <FeatureRoute feature='trading'>
                    <PrivateRoute perms={[EmailVerified, KYC, AccountNotDormant]}>
                      <CompanyContainer />
                    </PrivateRoute>
                  </FeatureRoute>
                }
              />
              <Route
                path='/dashboard/secondary-market'
                element={
                  <PrivateRoute perms={[EmailVerified, KYC, AccountNotDormant]}>
                    <SecondaryMarketContainer />
                  </PrivateRoute>
                }
              />
              <Route
                path='/dashboard/investments/:opportunityId'
                element={
                  <PrivateRoute perms={[EmailVerified, AccountNotDormant]}>
                    <DashboardSpecificInvestmentV2Container />
                  </PrivateRoute>
                }
              />
              <Route path='/partner' element={<PartnerLoginContainer />} />
              <Route path='/partner/set-password' element={<PartnerSetPasswordContainer />} />
              <Route path='/partner/investors' element={<PartnerInvestorsContainer />} />
              <Route path='/about' element={<AboutUsV2 />} />
              {/* <Route path='/about-v2' element={<AboutUsV2 />} /> */}
              <Route path='*' element={<NotFound />} />
            </Routes>
          </Suspense>
        </ChunkErrorBoundary>
      </div>
      {!isAuthFullscreenRoute && !isPartnerRoute && <Footer />}

      {showContactUsModal ? (
        <ContactUsModal
          active={showContactUsModal}
          subject={'Onboard'}
          handleOnClose={() => setShowContactUsModal(false)}
        />
      ) : null}
      {!isPartnerRoute && <DormancyAcknowledgementModal />}
      {!isPartnerRoute && <DormancyReminderModal />}
      {!isPartnerRoute && preference && <GlobalPhoneCollectionModal />}
      {/* Temporarily disabled to avoid conflict with Intercom */}
      {/* {isLoggedIn ? <InvestorFeedbackWidget /> : null} */}

      {/* Intercom test panel removed for production/development cleanliness */}

      {/*WhatsApp Support Icon*/}
      {/* {isLoggedIn && <WhatsAppSupportComponent />} */}
    </>
  )
}

const App = () => (
  <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ''}>
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  </GoogleOAuthProvider>
)

export default App
